import { FC, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { ProgressSpinner } from 'primereact/progressspinner';
import { ColumnProps } from 'primereact/column';

import { getSettlementDetail } from '@/services/getSettlementDetail';
import { Table } from '@/components';
import { TableData } from '@/components/Table/types';
import { formatDate } from '@/utils/formatDate';

type SettlementDetailsProps = {
  oid: number;
  isExport: string;
};

const SettlementDetails: FC<SettlementDetailsProps> = ({ oid, isExport }) => {
  const { t } = useTranslation(['importFCL', 'exportFCL', 'mySettlement']);

  const { data, isFetching: isLoading } = useQuery({
    queryKey: [['oidLiquidacion', oid]],
    queryFn: () => getSettlementDetail({ oidLiquidacion: oid }),
    refetchOnWindowFocus: false,
  });

  const TABLE_COLUMNS: ColumnProps[] = [
    {
      field: 'contenedor',
      header: t('mySettlement:container'),
      style: { fontWeight: 'bold' },
    },
    {
      field: 'fechaRetiro',
      header:
        isExport === 'Importación'
          ? t('importFCL:withdrawalDate')
          : t('exportFCL:withdrawalDateExport'),
      body: (row) => formatDate(row.fechaRetiro),
    },
    {
      field: 'observaciones',
      header: t('mySettlement:observations'),
    },
    {
      field: 'importe',
      header: `${t('mySettlement:amount')} (U$S)`,
      style: { display: 'flex', justifyContent: 'flex-end' },
      body: (row) =>
        Number(row.importe).toLocaleString('es-UY', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
  ];

  const total = useMemo(() => {
    const response = data?.reduce(
      (acc, item) => acc + (Number(item?.importe) ?? 0),
      0
    );
    return response?.toLocaleString('es-UY', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [data]);

  return (
    <div className="p-3">
      {isLoading ? (
        <div className="w-full  h-25rem flex align-items-center justify-center">
          <ProgressSpinner />
        </div>
      ) : (
        <>
          <p>
            <Trans
              t={t}
              i18nKey={t('mySettlement:detailMessage')}
              values={{
                counter: data?.length,
                oid,
              }}
              components={{ b: <b /> }}
            />
          </p>
          <br />
          <Table
            columns={TABLE_COLUMNS}
            data={data as unknown as TableData}
            filterDisplay="menu"
            selectionId="settlement-details"
            footer={
              <div className="w-full flex justify-content-between">
                <h3 className="text-md">{t('total')}</h3>
                <div className="flex gap-6">
                  <span>U$S </span>
                  <span>{total}</span>
                </div>
              </div>
            }
          />
        </>
      )}
    </div>
  );
};

export default SettlementDetails;
